/**
 * AppointmentsHistorial — Listado completo de citas con tabla, filtros y paginación
 * UX adaptable: tabla en desktop, cards en móvil
 * Referencia: 0260 — Historial de agendas
 */
import React, { useMemo, useState, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/en-gb";
import { useStyles } from "./styles";
import { i18n } from "../../translate/i18n";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const STATUS_COLORS = {
  pending: "#ed6c02",
  confirmed: "#2e7d32",
  completed: "#2e7d32",
  rescheduled: "#2e7d32",
  in_progress: "#1976d2",
  cancelled: "#d32f2f",
  no_show: "#757575"
};

export default function AppointmentsHistorial({ appointments, loading, onEdit }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState("asc");

  useEffect(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") moment.locale("en-gb");
    else if (lang === "pt") moment.locale("pt");
    else moment.locale("es");
  }, [i18n.language]);

  const orderLabel = order === "asc"
    ? i18n.t("appointmentsModule.historial.orderAsc")
    : i18n.t("appointmentsModule.historial.orderDesc");

  const sortedAppointments = useMemo(() => {
    const arr = [...appointments];
    arr.sort((a, b) => {
      const da = new Date(a.appointmentDate);
      const db = new Date(b.appointmentDate);
      const cmp = order === "asc" ? da - db : db - da;
      return cmp;
    });
    return arr;
  }, [appointments, order]);

  const paginatedAppointments = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedAppointments.slice(start, start + rowsPerPage);
  }, [sortedAppointments, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSort = () => setOrder((o) => (o === "asc" ? "desc" : "asc"));

  const formatDate = (dt) => (dt ? moment(dt).format("L") : "-");
  const formatTime = (dt) => (dt ? moment(dt).format("LT") : "-");
  const formatDateTime = (dt) => (dt ? moment(dt).format("L LT") : "-");
  const getStatusLabel = (s) => i18n.t(`appointmentsModule.status.${s}`, { defaultValue: s });
  const getStatusColor = (s) => STATUS_COLORS[s] || "#757575";

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography color="textSecondary">{i18n.t("appointmentsModule.historial.loading")}</Typography>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography color="textSecondary">{i18n.t("appointmentsModule.historial.empty")}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          {i18n.t("appointmentsModule.historial.emptyHint")}
        </Typography>
      </div>
    );
  }

  const countOrderLine = i18n.t("appointmentsModule.historial.countOrder", {
    count: appointments.length,
    order: orderLabel
  });

  const renderPagination = (mobile) => (
    <TablePagination
      component="div"
      count={appointments.length}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage={mobile ? i18n.t("appointmentsModule.historial.rowsPerPageShort") : i18n.t("appointmentsModule.historial.rowsPerPage")}
      labelDisplayedRows={({ from, to, count }) =>
        i18n.t("appointmentsModule.historial.displayedRows", { from, to, count })
      }
      style={mobile ? { marginTop: 16, overflow: "auto" } : { marginTop: 8 }}
    />
  );

  if (isMobile) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Typography variant="body2" color="textSecondary">{countOrderLine}</Typography>
          <Typography variant="caption" component="button" onClick={handleSort} style={{ cursor: "pointer", textDecoration: "underline", border: "none", background: "none" }}>
            {i18n.t("appointmentsModule.historial.changeOrder")}
          </Typography>
        </div>
        {paginatedAppointments.map((a) => (
          <Paper key={a.id} className={classes.historialCard} elevation={0}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {a.contactName || i18n.t("appointmentsModule.calendar.noName")}
                </Typography>
              <Typography variant="body2" color="textSecondary">
                {a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback")}
                {a.professionalName && ` · ${a.professionalName}`}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>{formatDateTime(a.appointmentDate)}</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                {[
                  a.confirmationCode ? `${i18n.t("appointmentsModule.historial.code")}: ${a.confirmationCode}` : null,
                  a.durationMin ? `${a.durationMin} min` : null,
                  a.modality ? i18n.t(`appointmentsModule.historial.modality.${a.modality}`, { defaultValue: a.modality }) : null,
                  a.locationDetail || null
                ].filter(Boolean).join(" · ")}
              </Typography>
            </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    padding: "2px 6px",
                    borderRadius: 4,
                    backgroundColor: `${getStatusColor(a.status)}20`,
                    color: getStatusColor(a.status)
                  }}
                >
                  {getStatusLabel(a.status)}
                </span>
                <Tooltip title={i18n.t("appointmentsModule.historial.edit")}>
                  <IconButton size="small" onClick={() => onEdit(a)} aria-label={i18n.t("appointmentsModule.historial.editAria")}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </Paper>
        ))}
        {renderPagination(true)}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <Typography variant="body2" color="textSecondary">{countOrderLine}</Typography>
        <Typography variant="caption" component="button" onClick={handleSort} style={{ cursor: "pointer", textDecoration: "underline", border: "none", background: "none" }}>
          {i18n.t("appointmentsModule.historial.changeOrder")}
        </Typography>
      </div>
      <TableContainer component={Paper} className={classes.historialTableWrap} elevation={0}>
        <Table size="small" stickyHeader aria-label={i18n.t("appointmentsModule.historial.tableAria")}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colDate")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colTime")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colContact")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colService")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colProfessional")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colDetails")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.historial.colStatus")}</TableCell>
              <TableCell className={classes.historialTh} align="right">{i18n.t("appointmentsModule.historial.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAppointments.map((a) => (
              <TableRow key={a.id} hover className={classes.historialRow}>
                <TableCell>{formatDate(a.appointmentDate)}</TableCell>
                <TableCell>{formatTime(a.appointmentDate)}</TableCell>
                <TableCell>{a.contactName || "—"}</TableCell>
                <TableCell>{a.serviceName || "—"}</TableCell>
                <TableCell>{a.professionalName || "—"}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {a.confirmationCode || "—"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {[
                      a.durationMin ? `${a.durationMin} min` : null,
                      a.modality ? i18n.t(`appointmentsModule.historial.modality.${a.modality}`, { defaultValue: a.modality }) : null,
                      a.locationDetail || null
                    ].filter(Boolean).join(" · ") || "—"}
                  </Typography>
                  {a.notes ? (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {a.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 4,
                      backgroundColor: `${getStatusColor(a.status)}20`,
                      color: getStatusColor(a.status)
                    }}
                  >
                    {getStatusLabel(a.status)}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={i18n.t("appointmentsModule.historial.editReschedule")}>
                    <IconButton size="small" onClick={() => onEdit(a)} aria-label={i18n.t("appointmentsModule.historial.editAria")}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {renderPagination(false)}
    </div>
  );
}
